import { useMemo } from 'react';
import { type MessageDescriptor, useIntl } from 'react-intl';

import { TITLES } from '../../../constants';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns all route titles and custom document titles used across
 * the expo-router screen options.
 *
 * Automatically formats every route title using `react-intl`.
 * To add a new title, just add it to `route-titles.ts` — no changes needed here.
 */
export const useRouteTitles = () => {
  const { formatMessage } = useIntl();

  return useMemo(() => formatMessages(TITLES, formatMessage), [formatMessage]);
};

// ─────────────────────────────────────────────────────────────────────────────

const formatMessages = <T extends Record<string, MessageDescriptor>>(
  messages: T,
  formatMessage: (descriptor: MessageDescriptor) => string,
): { [K in keyof T]: string } => {
  const entries = Object.entries(messages) as Array<
    [keyof T, MessageDescriptor]
  >;

  return Object.fromEntries(
    entries.map(([key, descriptor]) => [key, formatMessage(descriptor)]),
  ) as { [K in keyof T]: string };
};
